// 離線假資料 Spin 服務 — 對應 Unity SlotFramework FakeData。
// 隨機產生盤面，本地用 checkLines + payTable 計算連線贏分，使前端可離線完整運行。
import {
  ALL_MULTIPLIER_ARRAY,
  getAvailableMultiplierIndexes,
  MIN_LINE_MATCH,
  MULTIPLIER_WHEEL_VALUE,
  SYMBOL_NAMES,
  SYMBOL_WILD_INDEX,
} from '../constants'
import { toServerBalance } from '../../framework/net/balance'
import { lineOdds } from '../config/payTable'
import type { MGResult, BGResult, SlotResult } from '../model/SlotResult'
import { checkLines } from '../model/winCheck'
import type { SpinRequest, SpinService } from './SpinService'

export interface MockConfig {
  readonly reelCount: number
  readonly rowCount: number
  readonly paylines: readonly (readonly number[])[]
  /** 倍數轉輪停在 Wheel 符號（→ 進 Bonus 轉盤）的機率（0~1） */
  readonly bonusChance?: number
}

// 符號權重：Wild 較稀有，低階符號較常見
const SYMBOL_WEIGHTS = [2, 6, 7, 8, 12, 13, 14, 16]
const JACKPOT_TIERS = ['MINI', 'MINOR', 'MAJOR', 'GRAND'] as const

export class MockSpinService implements SpinService {
  private readonly config: MockConfig

  constructor(config: MockConfig) {
    this.config = config
  }

  async spin(request: SpinRequest): Promise<SlotResult> {
    const { reelCount, rowCount, paylines } = this.config
    // 停在 Wheel（→ Bonus）應為稀有事件；免費遊戲不再觸發。
    const wheelChance = this.config.bonusChance ?? 0.05
    const free = request.free === true

    const board = this.randomBoard(reelCount, rowCount)
    const lineResults = checkLines(board, paylines, SYMBOL_WILD_INDEX, MIN_LINE_MATCH)

    // 以伺服器單位（×10000）計算，使輸出金額與真實後端一致（前端再經 toClientBalance ÷10000）。
    const serverBet = toServerBalance(request.totalBet)
    const lineBet = Math.max(1, Math.floor(serverBet / Math.max(1, paylines.length)))
    const mgLineWin: bigint[] = []
    const mgLineCount: number[] = []
    const mgLineSymbol: number[] = []
    let mainWinValue = 0

    for (const line of lineResults) {
      const runLength = line.symbol >= 0 ? line.adjacentMatches + 1 : 0
      const win = line.symbol >= 0 ? Math.round(lineBet * lineOdds(line.symbol, runLength)) : 0
      mgLineWin.push(BigInt(win))
      mgLineCount.push(runLength) // 後端語意：連線符號總數（3 連 = 3，非中獎 = 0）
      mgLineSymbol.push(line.symbol)
      mainWinValue += win
    }

    const isExtraBet = request.buyType === 1
    const available = getAvailableMultiplierIndexes(isExtraBet)

    // 倍數轉輪停在 Wheel(0) 是唯一進 Bonus 轉盤演出的途徑（對應 Unity Slot001_MultiplierReels.CheckWin）。
    const landsOnWheel = !free && Math.random() < wheelChance
    let multiplierValue: number
    if (landsOnWheel) {
      multiplierValue = MULTIPLIER_WHEEL_VALUE // 0 = Wheel
    } else {
      // 停在一般倍數（不觸發 Bonus）：自可用集合排除 Wheel 後隨機挑一個。
      const nonWheel = available.filter((i) => ALL_MULTIPLIER_ARRAY[i] !== MULTIPLIER_WHEEL_VALUE)
      const picked = nonWheel[Math.floor(Math.random() * nonWheel.length)] ?? 1
      multiplierValue = ALL_MULTIPLIER_ARRAY[picked]
    }
    const triggerBonus = landsOnWheel
    if (multiplierValue > 1) {
      mainWinValue *= multiplierValue
    }

    const mgResult: MGResult = {
      mgSymbol: board,
      mgCombIndex: Math.floor(Math.random() * 100),
      mgReelIndex: Array.from({ length: reelCount }, () => Math.floor(Math.random() * 100)),
      mgLineSymbol,
      mgLineCount,
      mgLineWin,
      mgMultiplier: multiplierValue, // Wheel→0（進 Bonus），其餘為 ≥1 的倍數
      mainWin: BigInt(mainWinValue),
    }

    const bonusWinValue = triggerBonus
      ? Math.round(serverBet * (5 + Math.random() * 45))
      : 0
    const bgResult: BGResult = {
      bgWheelPairIndex: triggerBonus ? Math.floor(Math.random() * 6) : 0,
      bgInner: triggerBonus ? Math.floor(Math.random() * 8) : 0,
      bgOuter: triggerBonus ? Math.floor(Math.random() * 6) : 0,
      bonusWin: BigInt(bonusWinValue),
    }

    // 玩法旗標（免費遊戲中不再觸發；機率調高以利展示）
    const freeSpins = !free && Math.random() < 0.1 ? 6 + Math.floor(Math.random() * 5) : 0
    const jackpot =
      !free && Math.random() < 0.05
        ? {
            tier: JACKPOT_TIERS[Math.floor(Math.random() * JACKPOT_TIERS.length)],
            amount: BigInt(Math.round(serverBet * (50 + Math.random() * 600))),
          }
        : null
    const maxWin = !free && Math.random() < 0.03

    return {
      code: 0,
      mgResult,
      bgResult,
      totalWin: BigInt(mainWinValue + bonusWinValue),
      gameMode: free ? 1 : 0,
      buyType: request.buyType,
      ...(freeSpins > 0 ? { freeSpins } : {}),
      ...(jackpot !== null ? { jackpot } : {}),
      ...(maxWin ? { maxWin: true } : {}),
    }
  }

  private randomBoard(reelCount: number, rowCount: number): number[][] {
    const total = SYMBOL_WEIGHTS.reduce((sum, w) => sum + w, 0)
    const pick = (): number => {
      let roll = Math.random() * total
      for (let i = 0; i < SYMBOL_WEIGHTS.length; i++) {
        roll -= SYMBOL_WEIGHTS[i]
        if (roll <= 0) return i
      }
      return SYMBOL_NAMES.length - 1
    }
    return Array.from({ length: reelCount }, () =>
      Array.from({ length: rowCount }, () => pick()),
    )
  }
}
